import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, AlertTriangle, ChevronRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function TermsOfService() {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: language === 'es' ? 'Aceptación de Términos' : 'Acceptance of Terms',
      content: language === 'es' 
        ? 'Al acceder o utilizar los servicios de PeCashGlobal, usted acepta estar sujeto a estos Términos de Servicio...'
        : 'By accessing or using PeCashGlobal services, you agree to be bound by these Terms of Service...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Descripción de Servicios' : 'Description of Services',
      content: language === 'es'
        ? 'PeCashGlobal proporciona una plataforma tecnológica que permite a los clientes autorizar compras para familiares en comercios participantes en la República Dominicana...'
        : 'PeCashGlobal provides a technology platform that enables customers to authorize purchases for family members at participating merchants in the Dominican Republic...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Elegibilidad' : 'Eligibility',
      content: language === 'es'
        ? 'Para utilizar nuestros servicios, debe tener al menos 18 años de edad y ser residente legal de los Estados Unidos...'
        : 'To use our services, you must be at least 18 years of age and a legal resident of the United States...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Cuentas de Usuario' : 'User Accounts',
      content: language === 'es'
        ? 'Usted es responsable de mantener la confidencialidad de su cuenta y contraseña...'
        : 'You are responsible for maintaining the confidentiality of your account and password...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Términos de Transacción' : 'Transaction Terms',
      content: language === 'es'
        ? 'Las autorizaciones de compra están sujetas a límites de transacción, tarifas de servicio y disponibilidad de comerciantes participantes...'
        : 'Purchase authorizations are subject to transaction limits, service fees, and availability of participating merchants...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Política de Reembolsos' : 'Refund Policy',
      content: language === 'es'
        ? 'Los reembolsos pueden estar disponibles en ciertas circunstancias según lo determine PeCashGlobal a su exclusiva discreción...'
        : 'Refunds may be available in certain circumstances as determined by PeCashGlobal in its sole discretion...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Actividades Prohibidas' : 'Prohibited Activities',
      content: language === 'es'
        ? 'Está prohibido utilizar nuestros servicios para actividades ilegales, lavado de dinero, financiamiento del terrorismo...'
        : 'You are prohibited from using our services for illegal activities, money laundering, terrorism financing...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Propiedad Intelectual' : 'Intellectual Property',
      content: language === 'es'
        ? 'Todo el contenido, marcas registradas y materiales en PeCashGlobal son propiedad de PeCashGlobal o sus licenciantes...'
        : 'All content, trademarks, and materials on PeCashGlobal are owned by PeCashGlobal or its licensors...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Limitación de Responsabilidad' : 'Limitation of Liability',
      content: language === 'es'
        ? 'En la máxima medida permitida por la ley, PeCashGlobal no será responsable de ningún daño indirecto, incidental, especial o consecuente...'
        : 'To the maximum extent permitted by law, PeCashGlobal shall not be liable for any indirect, incidental, special, or consequential damages...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Resolución de Disputas' : 'Dispute Resolution',
      content: language === 'es'
        ? 'Cualquier disputa que surja de estos términos será resuelta mediante arbitraje vinculante...'
        : 'Any dispute arising from these terms shall be resolved through binding arbitration...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Ley Aplicable' : 'Governing Law',
      content: language === 'es'
        ? 'Estos términos se regirán e interpretarán de acuerdo con las leyes del Estado de [ESTADO]...'
        : 'These terms shall be governed by and construed in accordance with the laws of the State of [STATE]...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Cambios a los Términos' : 'Changes to Terms',
      content: language === 'es'
        ? 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Le notificaremos los cambios materiales...'
        : 'We reserve the right to modify these terms at any time. We will notify you of material changes...',
      status: 'LEGAL REVIEW REQUIRED',
    },
    {
      title: language === 'es' ? 'Contacto' : 'Contact',
      content: language === 'es'
        ? 'Para preguntas sobre estos términos, contáctenos en support@pecashglobal.com'
        : 'For questions about these terms, contact us at support@pecashglobal.com',
      status: 'PLACEHOLDER',
    },
  ];

  return (
    <Layout>
      <SEO
        title={language === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
        description={language === 'es' 
          ? 'Términos y condiciones de uso para los servicios de PeCashGlobal.'
          : 'Terms and conditions for using PeCashGlobal services.'}
        canonical="https://pecashglobal.com/terms"
        lang={language}
      />
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div {...fadeInUp} className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-full text-sm mb-6">
                <AlertTriangle className="w-4 h-4" />
                {language === 'es' ? 'Borrador - Revisión Legal Pendiente' : 'Draft - Legal Review Pending'}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {language === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
              </h1>
              <p className="text-slate-400">
                {language === 'es' ? 'Última actualización: [PENDIENTE]' : 'Last Updated: [PENDING]'}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          {/* Legal Review Notice */}
          <motion.div 
            {...fadeInUp}
            className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">
                  {language === 'es' ? 'Aviso Importante' : 'Important Notice'}
                </h3>
                <p className="text-amber-800 text-sm">
                  {language === 'es' 
                    ? 'Este documento es un borrador y requiere revisión legal antes de su publicación final. Las secciones marcadas con "[REVISIÓN LEGAL REQUERIDA]" contienen texto de marcador de posición que debe ser revisado y aprobado por un abogado.'
                    : 'This document is a draft and requires legal review before final publication. Sections marked with "[LEGAL REVIEW REQUIRED]" contain placeholder text that must be reviewed and approved by an attorney.'}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.section
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-slate-200 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-xl font-semibold text-slate-900">
                    {index + 1}. {section.title}
                  </h2>
                  {section.status !== 'PLACEHOLDER' && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                      {section.status}
                    </span>
                  )}
                </div>
                <p className="text-slate-600 leading-relaxed">
                  {section.content}
                </p>
              </motion.section>
            ))}
          </div>

          {/* Navigation */}
          <motion.div 
            {...fadeInUp}
            className="mt-12 flex flex-wrap gap-4 justify-center"
          >
            <Link 
              to="/privacy"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-colors"
            >
              <FileText className="w-4 h-4" />
              {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/compliance"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 border border-slate-200 rounded-full hover:bg-slate-50 transition-colors"
            >
              {language === 'es' ? 'Cumplimiento' : 'Compliance'}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
